import React, { useState } from 'react';
import { Helmet } from "react-helmet-async";
import { ChevronDown } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqData = [
    {
      q: "What services does Mobikode provide?",
      a: "Mobikode specializes in digital transformation, custom software development, enterprise application development, UI/UX design, performance engineering, and technology consulting. We help businesses build scalable digital products and optimize their IT infrastructure."
    },
    {
      q: "Do you offer custom software development?",
      a: "Yes. We build fully custom web, mobile, and enterprise applications tailored to your business requirements. Our solutions are scalable, secure, and built using modern technology stacks."
    },
    {
      q: "What industries do you work with?",
      a: "We work with startups, SMEs, and large enterprises across industries including real estate, fintech, healthcare, travel, e-commerce, logistics, and enterprise IT services."
    },
    {
      q: "Can Mobikode provide dedicated development teams?",
      a: "Absolutely. We offer staff augmentation and dedicated development teams, including frontend, backend, DevOps, QA engineers, and UI/UX designers to support your projects."
    },
    {
      q: "What technologies does Mobikode work with?",
      a: "Our team works with technologies including React, Angular, Node.js, Python, Java, .NET, cloud platforms (AWS, Azure, GCP), DevOps tools, and enterprise platforms such as Salesforce."
    },
    {
      q: "Do you support enterprise digital transformation projects?",
      a: "Yes. Mobikode partners with enterprises to modernize legacy systems, implement cloud migration strategies, improve performance engineering, and build secure, scalable digital ecosystems."
    },
    {
      q: "How do you ensure project quality and delivery timelines?",
      a: "We follow agile development methodologies with sprint-based delivery, continuous testing, code reviews, and transparent communication to ensure on-time and high-quality project delivery."
    },
    {
      q: "Do you provide post-development support and maintenance?",
      a: "Yes. We offer ongoing maintenance, monitoring, performance optimization, security updates, and long-term support under defined SLAs."
    },
    {
      q: "How do you handle data security and compliance?",
      a: "We implement secure coding practices, data encryption, role-based access controls, and compliance-aligned architectures to ensure data protection and regulatory adherence."
    },
    {
      q: "How can we start a project with Mobikode?",
      a: "You can reach out through our website (Mobikode.com) or contact our team directly to schedule a consultation. We begin with a discovery session to understand your business goals and propose a tailored technical solution."
    }
  ];

  return (
    <>
<Helmet>
    <title>
        FAQs | Mobikode – Technology Services & Solutions Questions
    </title>

    <meta
        name="description"
        content="Find answers to frequently asked questions about Mobikode's technology services, custom software development, Enterprise AI & ServiceNow Consulting, enterprise applications, cloud, AI, staff augmentation, security, and project delivery."
    />

    <meta
        name="keywords"
        content="Mobikode FAQs, Mobikode Frequently Asked Questions, Technology Services FAQs, Software Development FAQs, Custom Software Development, Enterprise AI & ServiceNow Consulting FAQs, Enterprise Application Development, Cloud Services, AI Automation Services, Staff Augmentation, Dedicated Development Teams, Technology Consulting, Software Engineering, IT Services, Project Delivery, Data Security, Software Maintenance"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/faqs"
    />

    <meta
        property="og:title"
        content="FAQs | Mobikode – Technology Services & Solutions Questions"
    />

    <meta
        property="og:description"
        content="Get answers about Mobikode's software development, Enterprise AI & ServiceNow Consulting, cloud, AI, enterprise technology, dedicated teams, security, maintenance, and project delivery services."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/faqs"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/what/img7.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="FAQs | Mobikode – Technology Services & Solutions Questions"
    />

    <meta
        name="twitter:description"
        content="Explore answers to common questions about Mobikode's technology solutions, software development, Enterprise AI & ServiceNow Consulting, cloud, AI, enterprise applications, security, and support services."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/what/img7.png"
    />
</Helmet>

      <div className="min-h-screen bg-white">

        <section className="relative w-full h-[450px] lg:h-[550px] flex items-center bg-[#000814] overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/what/img7.png"
              alt="Legal Background"
              className="w-full h-full object-cover opacity-80"
            />
            <div style={{ background: "linear-gradient(86.84deg, #000000 20%, rgba(0, 0, 0, 0) 79%)" }} className="absolute inset-0 "></div>
          </div>

          <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">

            <h1 className="text-white font-[600] text-4xl md:text-[55px] font-sora leading-[1.1] mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-gray-300 text-base md:text-lg font-graphik font-light max-w-[700px]">
              Find answers to common questions about our services, technologies, and project delivery.
            </p>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-[900px] mx-auto px-5">
            <div className="space-y-4">
              {faqData.map((item, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={index}
                    className="border border-gray-200 overflow-hidden transition-all duration-200"
                  >
                    <button
                      onClick={() => toggleAccordion(index)}
                      className="w-full flex items-center justify-between p-5 lg:p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className={`lg:text-[20px] text-[16px] font-[600] font-sora ${isOpen ? 'text-[#0E2A62]' : 'text-gray-700'}`}>
                        {item.q}
                      </span>
                      <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#0E2A62]' : 'text-gray-400'}`}>
                        <ChevronDown size={22} />
                      </div>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] border-t border-gray-100' : 'max-h-0'
                        } overflow-hidden`}
                    >
                      <div className="p-5 lg:p-6 bg-white">
                        <p className="text-gray-600 md:text-[16px] font-graphik font-[500] text-[14px] leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* 
            <div className="mt-20 border-t border-gray-100 pt-16 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-[#0E2A62] mb-4">
                Still have questions?
              </h3>
              <p className="text-gray-500 mb-8">
                Can't find the answer you're looking for? Reach out to our team.
              </p>
              <button className="bg-[#0E2A62] text-white px-10 py-4 rounded-md font-semibold hover:bg-[#163a82] transition-all">
                Contact Us Directly
              </button>
            </div> */}
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;